import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import api, { TOKEN_KEY, USER_KEY } from '../services/api';

export type AdminLevel = 'owner' | 'admin' | 'viewer';
export interface AdminUser { id: string; name: string; email: string; level?: AdminLevel }

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  /** Step 1: returns a short-lived challenge for the PIN step. */
  checkPassword: (email: string, password: string) => Promise<{ challenge: string; name: string }>;
  /** Step 2: the PIN, bound to the challenge from step 1. */
  verifyPin: (challenge: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

/** What the signed-in admin may do. The server enforces the same rules; this only decides what to show. */
export function useAccess() {
  const { user } = useAuth();
  const level: AdminLevel = user?.level || 'owner';
  return { level, canWrite: level === 'owner' || level === 'admin', isOwner: level === 'owner' };
}

function save(user: AdminUser) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(() => {
    try {
      const saved = localStorage.getItem(USER_KEY);
      return saved && localStorage.getItem(TOKEN_KEY) ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // An owner may change someone's level while they're signed in: refresh it from the server on load.
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return;
    api.get('/me').then(({ data }) => { setUser(data); save(data); }).catch(() => { /* 401 handled by the api client */ });
  }, []);

  const checkPassword = async (email: string, password: string) => (await api.post('/login', { email, password })).data;

  const verifyPin = async (challenge: string, pin: string) => {
    const { data } = await api.post('/login/verify-pin', { challenge, pin });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    save(data.user);
    setUser(data.user);
  };

  const logout = async () => {
    try { await api.post('/logout'); } catch { /* the session is ended locally either way */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, checkPassword, verifyPin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
